import {
  allFittedComponentsQuery,
  useDeleteFittedComponentMutation,
} from "@common/api/aircraft"
import { facilitiesQuery } from "@common/api/facilities"
import { currentUserQuery } from "@common/api/users"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { useHasPermission } from "@common/hooks/use-permissions"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import useCustomToast from "@/hooks/use-custom-toast"
import {
  defaultFacility,
  ReturnToStock,
  type StockReturn,
} from "../../-components/fitted-components/return-to-stock"
import {
  childrenOf,
  componentLabel,
  type FittedComponent,
  identityText,
} from "../../-lib/fitted-components"

export const Route = createFileRoute(
  "/_layout/aircraft_/$aircraftId/fitted-components/remove/$componentId",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "fitted_components:delete")) {
      throw redirect({ to: "/aircraft/$aircraftId", params, search })
    }
  },
  component: RemoveFittedComponent,
})

function descendantCount(
  component: FittedComponent,
  components: FittedComponent[],
): number {
  return childrenOf(component, components).reduce(
    (total, child) => total + 1 + descendantCount(child, components),
    0,
  )
}

function removalNote(count: number): string {
  const kept =
    "The part, its serial number and its readings stay on record, so it can be fitted again later."
  if (count === 0) return kept
  const fitted = count === 1 ? "1 component" : `${count} components`
  return `The ${fitted} fitted to it come off too. ${kept}`
}

function RemoveFittedComponent(): React.ReactElement {
  const { aircraftId, componentId } = Route.useParams()
  const { data } = useQuery(allFittedComponentsQuery(aircraftId))
  const components = data?.data ?? []
  const component = components.find((c) => c.id === componentId)
  const mutation = useDeleteFittedComponentMutation()
  const canReturn = useCanReturnToStock()
  const { data: facilities } = useQuery({
    ...facilitiesQuery(),
    enabled: canReturn,
  })
  const [stock, setStock] = useState<StockReturn | null>(null)
  const facility = stock
    ? (facilities?.data.find((f) => f.id === stock.facilityId) ??
      defaultFacility(facilities?.data ?? []))
    : undefined
  const navigate = useNavigate({ from: Route.fullPath })
  const showToast = useCustomToast()
  const back = (closePane: boolean) =>
    navigate({
      to: "/aircraft/$aircraftId",
      params: { aircraftId },
      search: (previous) => ({
        ...previous,
        tab: "fitted-components" as const,
        fitted_item: closePane ? undefined : previous.fitted_item,
      }),
    })

  const confirm = () =>
    mutation.mutate(
      {
        id: componentId,
        return_facility_id: facility?.id,
        return_condition: stock?.condition,
      },
      {
        onSuccess: (removed) => {
          const returned = facility
            ? ` · returned to stock at ${facility.name}`
            : ""
          showToast(
            "Component removed",
            `${identityText(removed)}${returned}`,
            "success",
          )
          back(true)
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={`Remove ${component ? componentLabel(component) : "component"}?`}
      description={
        component && removalNote(descendantCount(component, components))
      }
      action="Remove from aircraft"
      pendingAction="Removing…"
      pending={mutation.isPending}
      disabled={!component || (stock !== null && !facility)}
      onConfirm={confirm}
      onCancel={() => back(false)}
    >
      {canReturn && (
        <ReturnToStock
          facilities={facilities?.data ?? []}
          value={stock}
          onChange={setStock}
        />
      )}
    </ConfirmDialog>
  )
}

function useCanReturnToStock(): boolean {
  const canCreate = useHasPermission("part_balance:create")
  const canUpdate = useHasPermission("part_balance:update")
  const canReadFacilities = useHasPermission("facility:read")
  return canCreate && canUpdate && canReadFacilities
}
